import { computed, watch, type Ref } from 'vue'
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient
} from '@tanstack/vue-query'
import { ApiClient, type ApiRequestConfig } from '@/utils/api-client'
import { ENDPOINTS } from '@/api'
import {
  clampPosition,
  libraryContext,
  mergeProgress,
  nextStoryPage,
  positiveId
} from './adapters'
import { storyWrites } from '@/composables/stories/write-queue'
import type {
  CompletionSource,
  LibraryContext,
  StoryDetail,
  StoryOption,
  StoryPage,
  StoryProgress,
  StoryResponse
} from './types'

export const STORY_LOCALE = 'fa' // ApiClient's current Accept-Language; included in personalized keys.
const config = (signal?: AbortSignal): ApiRequestConfig => ({
  suppressGlobalErrorRedirects: true,
  retainUnauthorizedRedirect: true,
  signal
})
const validId = (id: string) => {
  if (!positiveId(id)) throw new TypeError('Invalid story ID')
  return id
}
export const storyKeys = {
  account: (account: string) => ['stories', account] as const,
  lists: (account: string) => ['stories', account, 'list'] as const,
  list: (account: string, context: LibraryContext) =>
    ['stories', account, 'list', STORY_LOCALE, context, 15] as const,
  detail: (account: string, id: string) =>
    ['stories', account, 'detail', STORY_LOCALE, id] as const
}

export async function getStories(
  context: Partial<LibraryContext>,
  page: number,
  signal?: AbortSignal
) {
  return ApiClient.get<StoryPage>(ENDPOINTS.Stories.List, {
    ...config(signal),
    params: { ...libraryContext(context), page, pageSize: 15 }
  })
}
export async function getStory(id: string, signal?: AbortSignal) {
  return ApiClient.get<StoryResponse<StoryDetail>>(
    ENDPOINTS.Stories.Detail(validId(id)),
    config(signal)
  )
}
export const openStory = async (id: string, signal?: AbortSignal) =>
  ApiClient.post<StoryResponse<StoryProgress>>(
    ENDPOINTS.Stories.Open(validId(id)),
    undefined,
    config(signal)
  )
export const saveStory = async (
  id: string,
  isSaved: boolean,
  signal?: AbortSignal
) =>
  ApiClient.put<StoryResponse<StoryProgress>>(
    ENDPOINTS.Stories.Saved(validId(id)),
    { isSaved },
    config(signal)
  )
export const updateStoryProgress = async (
  id: string,
  positionMs: number,
  signal?: AbortSignal
) =>
  ApiClient.put<StoryResponse<StoryProgress>>(
    ENDPOINTS.Stories.Progress(validId(id)),
    { positionMs: clampPosition(positionMs, 0) },
    config(signal)
  )
export const completeStory = async (
  id: string,
  source: CompletionSource,
  signal?: AbortSignal
) =>
  ApiClient.post<StoryResponse<StoryProgress>>(
    ENDPOINTS.Stories.Complete(validId(id)),
    { source },
    config(signal)
  )

export function useStories(account: Ref<string>, context: Ref<LibraryContext>) {
  const list = useInfiniteQuery({
    queryKey: computed(() => storyKeys.list(account.value, context.value)),
    queryFn: ({ queryKey, pageParam, signal }) =>
      getStories(queryKey[4], pageParam, signal),
    initialPageParam: 1,
    getNextPageParam: nextStoryPage,
    enabled: computed(() => Boolean(account.value)),
    retry: false,
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: false
  })
  const items = computed(() => {
    const unique = new Map()
    for (const page of list.data.value?.pages ?? [])
      for (const story of page.data ?? [])
        if (positiveId(story.id) && !unique.has(story.id))
          unique.set(story.id, story)
    return [...unique.values()] as StoryPage['data']
  })
  return { list, items }
}

export function useStoryDetail(account: Ref<string>, id: string) {
  return useQuery({
    queryKey: computed(() => storyKeys.detail(account.value, id)),
    queryFn: ({ signal }) => getStory(id, signal),
    enabled: computed(() => Boolean(account.value && positiveId(id))),
    retry: false,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // An entry revalidates progress, but no automatic refetch interrupts a working audio source.
    refetchOnMount: 'always'
  })
}
export function useStoryOptions(account: Ref<string>) {
  const options = (kind: 'languages' | 'levels') =>
    useQuery({
      queryKey: computed(() => ['story-options', account.value, STORY_LOCALE, kind]),
      queryFn: ({ signal }) =>
        ApiClient.get<StoryResponse<{ list: StoryOption[] }>>(
          kind === 'languages'
            ? ENDPOINTS.Languages.LanguagesList
            : ENDPOINTS.Stories.Levels,
          {
            ...config(signal),
            ...(kind === 'languages' ? { params: { orderType: 'ASC' } } : {})
          }
        ),
      enabled: computed(() => Boolean(account.value)),
      retry: false,
      staleTime: 300000,
      gcTime: 0
    })
  return { languages: options('languages'), levels: options('levels') }
}

export function mergeStoryCaches(
  client: QueryClient,
  account: string,
  id: string,
  progress: StoryProgress
) {
  client.setQueryData<StoryResponse<StoryDetail>>(
    storyKeys.detail(account, id),
    (old) =>
      old
        ? {
            ...old,
            data: {
              ...old.data,
              progress: mergeProgress(old.data.progress, progress)
            }
          }
        : old
  )
  for (const [key] of client.getQueriesData({
    queryKey: storyKeys.lists(account)
  })) {
    const context = key[4] as LibraryContext
    client.setQueryData<InfiniteData<StoryPage>>(key, (old) =>
      old
        ? {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.flatMap((story) => {
                if (story.id !== id) return [story]
                const updated = {
                  ...story,
                  progress: mergeProgress(story.progress, progress)
                }
                if (
                  (context.tab === 'saved' && !updated.progress.isSaved) ||
                  (context.tab === 'in_progress' &&
                    updated.progress.readingStatus !== 'in_progress')
                )
                  return []
                return [updated]
              })
            }))
          }
        : old
    )
  }
}

type StoryAction =
  | { kind: 'open' }
  | { kind: 'saved'; isSaved: boolean }
  | { kind: 'progress'; positionMs: number }
  | { kind: 'complete'; source: CompletionSource }
export function useStoryActions(account: Ref<string>) {
  const client = useQueryClient()
  return (id: string, action: StoryAction): Promise<StoryProgress> => {
    const owner = account.value
    return storyWrites.run(
      owner,
      id,
      async (signal) => {
        const result =
          action.kind === 'open'
            ? await openStory(id, signal)
            : action.kind === 'saved'
              ? await saveStory(id, action.isSaved, signal)
              : action.kind === 'progress'
                ? await updateStoryProgress(id, action.positionMs, signal)
                : await completeStory(id, action.source, signal)
        if (signal.aborted)
          throw new DOMException('Story session ended', 'AbortError')
        mergeStoryCaches(client, owner, id, result.data)
        if (action.kind !== 'progress')
          void client.invalidateQueries({ queryKey: storyKeys.lists(owner) })
        return result.data
      },
      async (signal) => {
        // A previous write may have reached the server despite a network error. Read first;
        // the user-initiated retry still sends the explicit desired state, never a toggle.
        const fresh = await getStory(id, signal)
        if (!signal.aborted && storyWrites.isAccount(owner)) {
          client.setQueryData(storyKeys.detail(owner, id), fresh)
          void client.invalidateQueries({ queryKey: storyKeys.lists(owner) })
        }
      }
    )
  }
}

/** Mounted once at the app root, even when the learner signs out outside the story routes. */
export function useStoryAccountCleanup(account: Ref<string>) {
  const client = useQueryClient()
  watch(
    account,
    (current, previous) => {
      storyWrites.setAccount(current)
      if (previous !== undefined) {
        for (const key of [
          ['stories', previous],
          ['story-options', previous]
        ]) {
          void client.cancelQueries({ queryKey: key })
          client.removeQueries({ queryKey: key })
        }
      }
    },
    { immediate: true, flush: 'sync' }
  )
}
